import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import OrderTypeBadge from "@/components/OrderTypeBadge";
import EditOrderDialog from "@/components/EditOrderDialog";
import { toast } from "sonner";
import { Eye, ExternalLink, Pencil, Trash2 } from "lucide-react";

const DELIVERY_STATUSES = ["Pending", "In Transit", "Delivered", "Undelivered", "Cancelled"];

function getOrdersTitle(role) {
  if (role === "operations") return "Order Fulfillment";
  if (role === "sales") return "My Orders";
  return "All Orders";
}

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [dStatus, setDStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [fulfillForm, setFulfillForm] = useState({ delivery_link: "", dispatch_notes: "" });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE = 100;

  const load = useCallback(async (append = false) => {
    const params = { limit: PAGE, offset: append ? orders.length : 0 };
    if (fStatus !== "all") params.fulfillment_status = fStatus;
    if (dStatus !== "all") params.delivery_status = dStatus;
    if (source !== "all") params.order_source = source;
    if (search) params.search = search;
    setLoading(true);
    try {
      const countParams = { ...params };
      delete countParams.limit;
      delete countParams.offset;
      const [{ data }, { data: cnt }] = await Promise.all([
        api.get("/orders", { params }),
        append ? Promise.resolve({ data: { total } }) : api.get("/orders/count", { params: countParams }),
      ]);
      setOrders((prev) => (append ? [...prev, ...data] : data));
      if (!append) setTotal(cnt.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [fStatus, dStatus, source, search, orders.length, total]);

  useEffect(() => { load(false); }, [fStatus, dStatus, source]);   // reload on filter change

  const onSearch = (e) => { e.preventDefault(); load(false); };

  const openDetail = (o) => {
    setDetail(o);
    setFulfillForm({ delivery_link: o.delivery_link || "", dispatch_notes: o.dispatch_notes || "" });
  };

  const markFulfilled = async () => {
    try {
      await api.put(`/orders/${detail.id}/fulfill`, {
        fulfillment_status: "Fulfilled",
        delivery_link: fulfillForm.delivery_link,
        dispatch_notes: fulfillForm.dispatch_notes,
      });
      toast.success("Order fulfilled");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDispatch = async () => {
    try {
      await api.put(`/orders/${detail.id}/fulfill`, {
        delivery_link: fulfillForm.delivery_link,
        dispatch_notes: fulfillForm.dispatch_notes,
      });
      toast.success("Saved");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateDelivery = async (id, status) => {
    try {
      await api.put(`/orders/${id}/delivery`, { delivery_status: status });
      toast.success("Delivery status updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.order_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success(`Order ${o.order_id} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const title = getOrdersTitle(user.role);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">View and manage orders</p>
      </div>

      <Card className="p-4">
        <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input placeholder="Search order ID, customer, phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="md:col-span-2" data-testid="orders-search" />
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="filter-fulfillment"><SelectValue placeholder="Fulfillment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fulfillment</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Fulfilled">Fulfilled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dStatus} onValueChange={setDStatus}>
            <SelectTrigger data-testid="filter-delivery"><SelectValue placeholder="Delivery" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Delivery</SelectItem>
              {DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="Manual Order">Manual Order</SelectItem>
              <SelectItem value="Claimed Order">Claimed Order</SelectItem>
            </SelectContent>
          </Select>
        </form>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Order ID</TableHead>
                <TableHead>Source</TableHead>
                {user.role !== "sales" && <TableHead>BDM</TableHead>}
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Order Type</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Fulfillment</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground py-8">No orders</TableCell></TableRow>}
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.order_id}`}>
                  <TableCell className="font-mono text-xs sticky left-0 z-10 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">{o.order_id}</TableCell>
                  <TableCell><StatusBadge status={o.order_source} /></TableCell>
                  {user.role !== "sales" && <TableCell>{o.agent_name}</TableCell>}
                  <TableCell className="font-medium">{o.customer_name}</TableCell>
                  <TableCell>{o.phone}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs" title={(o.items || []).map((i) => `${i.sku_name} x${i.quantity}`).join(", ")}>
                    {(o.items || []).map((i) => `${i.sku_name} x${i.quantity}`).join(", ") || "—"}
                  </TableCell>
                  <TableCell>₹{Number(o.total_order_amount).toFixed(2)}</TableCell>
                  <TableCell><OrderTypeBadge name={o.order_type_tag_name} color={o.order_type_tag_color} /></TableCell>
                  <TableCell><StatusBadge status={o.order_type} /></TableCell>
                  <TableCell><StatusBadge status={o.fulfillment_status} /></TableCell>
                  <TableCell>
                    {user.role === "sales" ? (
                      <Select value={o.delivery_status} onValueChange={(v) => updateDelivery(o.id, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`delivery-select-${o.order_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <StatusBadge status={o.delivery_status} />}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {o.delivery_link && (
                        <a href={o.delivery_link} target="_blank" rel="noreferrer" data-testid={`track-link-${o.order_id}`}>
                          <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                        </a>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openDetail(o)} data-testid={`view-order-${o.order_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {user.role === "admin" && (
                        <Button variant="ghost" size="sm" onClick={() => deleteOrder(o)} data-testid={`delete-order-${o.order_id}`} title="Delete order">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between p-3 border-t bg-muted/20 text-xs text-muted-foreground">
          <div data-testid="orders-showing">Showing {orders.length} of {total} orders</div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={loading || orders.length >= total}
            data-testid="orders-load-more"
          >
            {loading ? "Loading…" : orders.length >= total ? "All loaded" : "Load more"}
          </Button>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Order {detail.order_id}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div><div className="text-xs text-muted-foreground">Source</div><StatusBadge status={detail.order_source} /></div>
                  <div><div className="text-xs text-muted-foreground">BDM</div><div>{detail.agent_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Customer</div><div>{detail.customer_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Phone</div><div>{detail.phone}</div></div>
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Address</div><div>{detail.address || "—"}</div></div>
                  <div><div className="text-xs text-muted-foreground">Order Type</div><StatusBadge status={detail.order_type} /></div>
                  <div><div className="text-xs text-muted-foreground">Total</div><div className="font-medium">₹{Number(detail.total_order_amount).toFixed(2)}</div></div>
                  {Number(detail.other_charges_amount || 0) > 0 && (
                    <div className="col-span-2 text-xs text-muted-foreground">
                      Includes other charges: ₹{Number(detail.other_charges_amount).toFixed(2)}
                      {detail.other_charges_note ? ` (${detail.other_charges_note})` : ""}
                    </div>
                  )}
                </div>
                {detail.items && detail.items.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-2">Items</div>
                    <Table>
                      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Qty</TableHead><TableHead>Price</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {detail.items.map((i, idx) => (
                          <TableRow key={`${i.sku_id}-${idx}`}><TableCell>{i.sku_name}</TableCell><TableCell>{i.quantity}</TableCell><TableCell>₹{i.selling_price}</TableCell><TableCell>₹{i.line_total}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {detail.payment_screenshot && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-2">Payment Screenshot</div>
                    <a href={`/api/files/${detail.payment_screenshot}`} target="_blank" rel="noreferrer" className="text-sm underline text-blue-600">
                      View attachment
                    </a>
                  </div>
                )}
                {detail.dispatch_notes && user.role !== "operations" && user.role !== "admin" && (
                  <div className="border-t border-border pt-4 space-y-1" data-testid="ops-dispatch-notes-readonly">
                    <div className="text-xs text-muted-foreground">Notes from Operations</div>
                    <div className="whitespace-pre-wrap text-sm bg-muted/40 rounded-md p-3 border border-border">{detail.dispatch_notes}</div>
                  </div>
                )}
                {(user.role === "operations" || user.role === "admin") && (
                  <div className="border-t border-border pt-4 space-y-3">
                    <h3 className="font-display font-semibold">Fulfillment</h3>
                    <div className="space-y-2">
                      <Label>Delivery link / tracking</Label>
                      <Input value={fulfillForm.delivery_link} onChange={(e) => setFulfillForm({ ...fulfillForm, delivery_link: e.target.value })} placeholder="https://..." data-testid="fulfill-link-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Dispatch notes</Label>
                      <Textarea value={fulfillForm.dispatch_notes} onChange={(e) => setFulfillForm({ ...fulfillForm, dispatch_notes: e.target.value })} data-testid="fulfill-notes-input" />
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {user.role === "admin" && (
                  <Button variant="outline" onClick={() => { setEditing(detail); setDetail(null); }} data-testid="edit-order-btn">
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </Button>
                )}
                {(user.role === "operations" || user.role === "admin") && (
                  <>
                    <Button variant="outline" onClick={saveDispatch} data-testid="save-dispatch-btn">Save</Button>
                    {detail.fulfillment_status !== "Fulfilled" && (
                      <Button onClick={markFulfilled} data-testid="mark-fulfilled-btn">Mark Fulfilled</Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <EditOrderDialog
        order={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}
